import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  QrCode,
  MagnifyingGlass,
  Motorcycle,
  BatteryHigh,
  Wrench,
  UserPlus,
  User,
  X,
  Plus,
  Database,
  CloudArrowDown,
} from "@phosphor-icons/react";
import Layout from "../components/Layout";
import QRScanner from "../components/QRScanner";
import { toast } from "sonner";
import ClientFormModal from "../components/ClientFormModal";
import { formatDocument } from "../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function SearchPage() {
  const navigate = useNavigate();
  const [searchType, setSearchType] = useState("chassis"); // 'chassis' | 'battery'
  const [searchValue, setSearchValue] = useState("");
  const [showScanner, setShowScanner] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const searchingRef = useRef(false);

  const [showClientModal, setShowClientModal] = useState(false);
  const [showNewClientModal, setShowNewClientModal] = useState(false);
  const [internalClients, setInternalClients] = useState([]);
  const [externalClients, setExternalClients] = useState([]);
  const [clientSource, setClientSource] = useState("internal");
  const [selectedClientId, setSelectedClientId] = useState("");
  const [loadingClients, setLoadingClients] = useState(false);
  const [linkingClient, setLinkingClient] = useState(false);

  const extractChassisFromUrl = (value) => {
    const lowerValue = value.toLowerCase();
    if (lowerValue.includes("/rastreio/")) {
      const match = value.match(/\/rastreio\/([^\/\?#]+)/i);
      if (match && match[1]) {
        return decodeURIComponent(match[1]).toUpperCase();
      }
    }
    return value.trim().toUpperCase();
  };

  const handleSearch = async (value = searchValue) => {
    let searchTerm = value.trim();

    if (!searchTerm) {
      setError("Digite um valor para buscar");
      return;
    }

    if (searchingRef.current) return;
    searchingRef.current = true;

    setLoading(true);
    setError("");

    searchTerm = extractChassisFromUrl(searchTerm);
    setSearchValue(searchTerm);

    try {
      const endpoint =
        searchType === "chassis"
          ? `${API}/tracking/chassis/${encodeURIComponent(searchTerm)}`
          : `${API}/tracking/battery/${encodeURIComponent(searchTerm)}`;

      const response = await axios.get(endpoint);
      setResult(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError(
          searchType === "chassis"
            ? "Moto não encontrada com este chassi"
            : "Bateria não vinculada a nenhuma moto",
        );
      } else {
        setError("Erro ao realizar busca");
      }
    } finally {
      setLoading(false);
      searchingRef.current = false;
    }
  };

  const handleScan = (code) => {
    const extracted = extractChassisFromUrl(code);
    setSearchValue(extracted);
    setShowScanner(false);
    if (!loading) {
      handleSearch(extracted);
    }
  };

  const fetchClientsList = async (source) => {
    setLoadingClients(true);
    try {
      if (source === "internal") {
        const res = await axios.get(`${API}/clients`, {
          withCredentials: true,
        });
        setInternalClients(res.data);
      } else {
        const res = await axios.get(`${API}/external-clients`, {
          withCredentials: true,
        });
        setExternalClients(res.data);
      }
    } catch (err) {
      toast.error(
        `Erro ao buscar clientes ${source === "internal" ? "internos" : "do MV"}`,
      );
    } finally {
      setLoadingClients(false);
    }
  };

  const openClientModal = async () => {
    setShowClientModal(true);
    if (clientSource === "internal" && internalClients.length === 0) {
      await fetchClientsList("internal");
    } else if (clientSource === "external" && externalClients.length === 0) {
      await fetchClientsList("external");
    }
  };

  const handleSourceChange = async (newSource) => {
    setClientSource(newSource);
    setSelectedClientId("");

    if (newSource === "internal" && internalClients.length === 0) {
      await fetchClientsList("internal");
    } else if (newSource === "external" && externalClients.length === 0) {
      await fetchClientsList("external");
    }
  };

  const handleLinkClient = async () => {
    if (!selectedClientId) return;

    const activeList =
      clientSource === "internal" ? internalClients : externalClients;
    const clientToLink = activeList.find((c) => c.id === selectedClientId);

    const payload = {
      id: clientToLink.id,
      name: clientToLink.name,
      document: clientToLink.document || "",
      phone: clientToLink.phone || "",
      email: clientToLink.email || "",
    };

    setLinkingClient(true);
    try {
      await axios.post(`${API}/motorcycles/${result.id}/link-client`, payload, {
        withCredentials: true,
      });
      toast.success("Cliente vinculado com sucesso!");
      setShowClientModal(false);
      handleSearch();
    } catch (err) {
      if (err.response?.status === 400) {
        toast.error(err.response.data.detail);
      } else {
        toast.error("Erro ao vincular cliente");
      }
    } finally {
      setLinkingClient(false);
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "completed":
        return "Finalizada";
      case "assembly":
        return "Em Montagem";
      case "revision":
        return "Em Revisão";
      case "revised":
        return "Revisão Concluída";
      default:
        return "Pendente";
    }
  };

  const currentClientsList =
    clientSource === "internal" ? internalClients : externalClients;

  return (
    <Layout title="Consulta">
      <div className="space-y-4 pb-4">
        {/* Search Type Tabs */}
        <div className="flex bg-[#121212] rounded-lg p-1 border border-white/10">
          <button
            onClick={() => {
              setSearchType("chassis");
              setResult(null);
              setError("");
            }}
            className={`flex-1 py-3 px-2 sm:px-4 rounded-md flex items-center justify-center gap-2 transition-colors text-sm sm:text-base ${
              searchType === "chassis"
                ? "bg-[#00E676] text-black font-medium"
                : "text-white"
            }`}
          >
            <Motorcycle size={20} />
            <span className="truncate">Chassi</span>
          </button>
          <button
            onClick={() => {
              setSearchType("battery");
              setResult(null);
              setError("");
            }}
            className={`flex-1 py-3 px-2 sm:px-4 rounded-md flex items-center justify-center gap-2 transition-colors text-sm sm:text-base ${
              searchType === "battery"
                ? "bg-[#00E676] text-black font-medium"
                : "text-white"
            }`}
          >
            <BatteryHigh size={20} />
            <span className="truncate">Bateria</span>
          </button>
        </div>

        {/* Search Input */}
        <div className="space-y-3">
          <div className="relative w-full">
            <MagnifyingGlass
              size={20}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A1A1AA] shrink-0"
            />
            <input
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="input-field !pl-12 !pr-14 w-full font-mono text-sm sm:text-base h-12"
              placeholder={
                searchType === "chassis"
                  ? "Número do chassi..."
                  : "Código da bateria..."
              }
            />
            <button
              onClick={() => setShowScanner(true)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center rounded-lg bg-[#1A1A1A] hover:bg-[#252525] transition-colors shrink-0"
            >
              <QrCode size={20} />
            </button>
          </div>

          <button
            onClick={() => handleSearch()}
            disabled={loading}
            className="btn-primary w-full py-3"
          >
            {loading ? "Buscando..." : "Buscar"}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="card p-4 border-[#FF3B30]/30 bg-[#FF3B30]/5 text-center">
            <p className="text-[#FF3B30] text-sm sm:text-base">{error}</p>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="card p-4 sm:p-5 space-y-4">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-[#A1A1AA]">Chassi</p>
                <p className="font-mono font-bold text-lg sm:text-xl truncate">
                  {result.chassis_sn}
                </p>
              </div>
              <div className="self-start sm:self-auto shrink-0">
                <span
                  className={`inline-block px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${
                    result.status === "completed"
                      ? "bg-[#00E676]/10 text-[#00E676]"
                      : result.status === "assembly"
                        ? "bg-yellow-500/10 text-yellow-500"
                        : result.status === "revision"
                          ? "bg-orange-500/10 text-orange-500"
                          : result.status === "revised"
                            ? "bg-blue-500/10 text-blue-500"
                            : "bg-[#A1A1AA]/10 text-[#A1A1AA]"
                  }`}
                >
                  {getStatusText(result.status)}
                </span>
              </div>
            </div>

            {/* Info */}
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/10">
              <div className="min-w-0">
                <p className="text-xs sm:text-sm text-[#A1A1AA]">Modelo</p>
                <p className="font-medium text-sm sm:text-base truncate">
                  {result.model}
                </p>
              </div>
              <div>
                <p className="text-xs sm:text-sm text-[#A1A1AA]">Voltagem</p>
                <p className="font-medium text-sm sm:text-base">
                  {result.voltage}V
                </p>
              </div>
            </div>

            {/* CLIENT INFO HIGHLIGHT */}
            {result.client ? (
              <div className="pt-4 border-t border-white/10">
                <div className="bg-[#00E676]/10 border border-[#00E676]/20 rounded-xl p-4 flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <User size={16} className="text-[#00E676]" />
                      <p className="text-xs sm:text-sm text-[#00E676] font-medium uppercase tracking-wider">
                        Cliente Vinculado
                      </p>
                    </div>
                    <p className="font-bold text-base sm:text-lg text-white truncate">
                      {result.client.name}
                    </p>
                    {result.client.document && (
                      <p className="text-xs text-[#A1A1AA] font-mono mt-1">
                        CPF/CNPJ: {formatDocument(result.client.document)}
                      </p>
                    )}
                  </div>
                  <div className="w-10 h-10 rounded-full bg-[#00E676]/20 flex items-center justify-center shrink-0">
                    <User size={20} className="text-[#00E676]" weight="fill" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="pt-4 border-t border-white/10">
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 flex items-center gap-3">
                  <UserPlus size={24} className="text-yellow-500 shrink-0" />
                  <p className="text-xs sm:text-sm text-yellow-500/90 font-medium">
                    Nenhum cliente está vinculado a esta moto no momento.
                  </p>
                </div>
              </div>
            )}

            {/* Batteries */}
            <div className="pt-4 border-t border-white/10">
              <p className="text-xs sm:text-sm text-[#A1A1AA] mb-2 font-medium">
                Baterias ({result.batteries.length}/
                {result.voltage === 60 ? 5 : 6})
              </p>
              <div className="flex flex-wrap gap-2">
                {result.batteries.map((bat, idx) => (
                  <span
                    key={idx}
                    className={`px-2 py-1 rounded text-xs sm:text-sm font-mono break-all sm:break-normal ${
                      result.searched_battery === bat
                        ? "bg-[#00E676] text-black font-bold"
                        : "bg-[#1A1A1A] border border-white/5"
                    }`}
                  >
                    {bat}
                  </span>
                ))}
                {result.batteries.length === 0 && (
                  <span className="text-[#A1A1AA] text-xs sm:text-sm">
                    Nenhuma bateria vinculada
                  </span>
                )}
              </div>
            </div>

            {/* Dates */}
            <div className="pt-4 border-t border-white/10 grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs sm:text-sm text-[#A1A1AA]">
                  Cadastrada em
                </p>
                <p className="text-xs sm:text-sm font-medium mt-0.5">
                  {new Date(result.created_at).toLocaleDateString("pt-BR")}
                </p>
              </div>
              {result.completed_at && (
                <div>
                  <p className="text-xs sm:text-sm text-[#A1A1AA]">
                    Finalizada em
                  </p>
                  <p className="text-xs sm:text-sm font-medium mt-0.5">
                    {new Date(result.completed_at).toLocaleDateString("pt-BR")}
                  </p>
                </div>
              )}
              {result.last_revision_at && (
                <div>
                  <p className="text-xs sm:text-sm text-[#A1A1AA]">
                    Última Revisão
                  </p>
                  <p className="text-xs sm:text-sm font-medium mt-0.5">
                    {new Date(result.last_revision_at).toLocaleDateString(
                      "pt-BR",
                    )}
                  </p>
                </div>
              )}
              {result.revision_count > 0 && (
                <div>
                  <p className="text-xs sm:text-sm text-[#A1A1AA]">Revisões</p>
                  <p className="text-xs sm:text-sm font-medium mt-0.5">
                    {result.revision_count}
                  </p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="pt-4 border-t border-white/10 space-y-3">
              <button
                onClick={openClientModal}
                className="btn-secondary w-full flex items-center justify-center gap-2 py-3 bg-purple-500/10 text-purple-400 border-purple-500/20 hover:bg-purple-500/20"
              >
                <UserPlus size={20} />
                {result.client
                  ? "Alterar Cliente Vinculado"
                  : "Vincular Cliente à Moto"}
              </button>

              {/* Revision Button - Only for completed or revised motos */}
              {(result.status === "completed" ||
                result.status === "revised") && (
                <button
                  onClick={() => navigate(`/revisao/${result.id}`)}
                  className="btn-primary w-full flex items-center justify-center gap-2 py-3"
                >
                  <Wrench size={20} />
                  <span>
                    {result.status === "revised"
                      ? "Nova Revisão"
                      : "Iniciar Revisão"}
                  </span>
                </button>
              )}

              {/* Continue Revision Button - Only for revision in progress */}
              {result.status === "revision" && (
                <button
                  onClick={() => navigate(`/revisao/${result.id}`)}
                  className="btn-primary w-full flex items-center justify-center gap-2 py-3 bg-yellow-500 hover:bg-yellow-600 text-black"
                >
                  <Wrench size={20} />
                  <span>Continuar Revisão</span>
                </button>
              )}

              {/* Continue Assembly Button - Only for pending/assembly */}
              {(result.status === "pending" ||
                result.status === "assembly") && (
                <button
                  onClick={() => navigate(`/motos/${result.id}`)}
                  className="btn-primary w-full flex items-center justify-center gap-2 py-3"
                >
                  Continuar Montagem
                </button>
              )}

              <button
                onClick={() => navigate(`/rastreio/${result.chassis_sn}`)}
                className="btn-secondary w-full py-3"
              >
                Ver Rastreabilidade Completa
              </button>
            </div>
          </div>
        )}
      </div>

      {/* QR Scanner Modal */}
      {showScanner && (
        <QRScanner onScan={handleScan} onClose={() => setShowScanner(false)} />
      )}

      {/* Modal de Vincular Cliente */}
      {showClientModal && (
        <div className="fixed inset-0 bg-black/90 z-[50] flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-[#121212] rounded-xl p-5 sm:p-6 w-full max-w-sm shadow-2xl border border-white/10 my-auto will-change-transform">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-lg">Vincular Cliente</h3>
              <button
                onClick={() => setShowClientModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#1A1A1A] transition-colors"
              >
                <X size={20} className="text-[#A1A1AA]" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="flex bg-[#1A1A1A] rounded-lg p-1 border border-white/10">
                <button
                  onClick={() => handleSourceChange("internal")}
                  className={`flex-1 py-2 text-xs sm:text-sm rounded-md transition-colors flex items-center justify-center gap-1.5 ${
                    clientSource === "internal"
                      ? "bg-purple-500 text-white font-medium"
                      : "text-[#A1A1AA] hover:text-white"
                  }`}
                >
                  <Database size={16} /> Local
                </button>
                <button
                  onClick={() => handleSourceChange("external")}
                  className={`flex-1 py-2 text-xs sm:text-sm rounded-md transition-colors flex items-center justify-center gap-1.5 ${
                    clientSource === "external"
                      ? "bg-purple-500 text-white font-medium"
                      : "text-[#A1A1AA] hover:text-white"
                  }`}
                >
                  <CloudArrowDown size={16} /> MasterVendas
                </button>
              </div>

              <p className="text-xs text-[#A1A1AA] leading-relaxed">
                {clientSource === "internal"
                  ? "Buscando da base local. Você pode cadastrar novos rapidamente."
                  : "Buscando da base do MV. Pode demorar alguns segundos."}
              </p>

              {loadingClients ? (
                <div className="flex flex-col items-center justify-center py-6 gap-3">
                  <div className="animate-spin w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full"></div>
                  <p className="text-xs text-[#A1A1AA]">
                    Carregando clientes...
                  </p>
                </div>
              ) : (
                <div>
                  <label className="text-xs sm:text-sm text-[#A1A1AA] block mb-2 font-medium ml-1">
                    Selecione o Cliente *
                  </label>
                  <div className="flex gap-2">
                    <select
                      value={selectedClientId}
                      onChange={(e) => setSelectedClientId(e.target.value)}
                      className="!h-12 input-field w-full truncate cursor-pointer"
                    >
                      <option value="">Selecione na lista...</option>
                      {currentClientsList.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} {c.document ? `(${c.document})` : ""}
                        </option>
                      ))}
                    </select>

                    {clientSource === "internal" && (
                      <button
                        onClick={() => setShowNewClientModal(true)}
                        title="Cadastrar Novo Cliente Local"
                        className="h-12 w-12 flex-shrink-0 flex items-center justify-center bg-[#1A1A1A] border border-white/10 rounded-xl hover:bg-[#00E676]/20 hover:text-[#00E676] hover:border-[#00E676]/30 transition-all"
                      >
                        <Plus size={20} weight="bold" />
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <button
                  onClick={() => setShowClientModal(false)}
                  className="btn-secondary w-full sm:flex-1 h-12"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleLinkClient}
                  disabled={
                    !selectedClientId || linkingClient || loadingClients
                  }
                  className="btn-primary w-full sm:flex-1 h-12 font-bold disabled:opacity-50"
                >
                  {linkingClient ? "Vinculando..." : "Confirmar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showNewClientModal && (
        <ClientFormModal
          API={API}
          onClose={() => setShowNewClientModal(false)}
          onSuccess={(newClient) => {
            setInternalClients((prev) => [...prev, newClient]);
            setSelectedClientId(newClient.id);
          }}
        />
      )}
    </Layout>
  );
}
